import React from "react";
import { SEO } from "../components/SEO";
import { BIZ } from "../lib/constants";

export default function Accessibility() {
    return (
        <>
            <SEO
                title="Accessibility"
                description="Liquor Studio's commitment to accessibility — WCAG 2.1 AA conformance and how to report issues."
                path="/accessibility"
            />

            <section
                style={{ paddingTop: 160, paddingBottom: 64, borderBottom: "1px solid var(--ls-border)" }}
            >
                <div className="ls-container max-w-3xl">
                    <span className="eyebrow">Legal</span>
                    <h1
                        className="font-display mt-5 text-white"
                        style={{
                            fontSize: "clamp(40px, 6vw, 64px)",
                            fontWeight: 500,
                            lineHeight: 1.05,
                            letterSpacing: "-0.02em",
                        }}
                    >
                        Accessibility Statement
                    </h1>
                    <p className="mt-4 text-sm" style={{ color: "var(--ls-text-muted)" }}>
                        Last updated: February 1, 2026
                    </p>
                </div>
            </section>

            <section className="section-y" data-testid="accessibility-content">
                <div
                    className="ls-container max-w-3xl"
                    style={{ color: "var(--ls-text-secondary)", fontSize: 16, lineHeight: 1.8 }}
                >
                    <p>
                        Liquor Studio is committed to ensuring that our website is accessible to
                        everyone, including people with disabilities.
                    </p>

                    <h2 className="font-display text-white mt-12" style={{ fontSize: 26, fontWeight: 500 }}>
                        Our commitment
                    </h2>
                    <p className="mt-4">
                        We strive to conform to the{" "}
                        <strong className="text-white">
                            Web Content Accessibility Guidelines (WCAG) 2.1 Level AA
                        </strong>
                        . This includes:
                    </p>
                    <ul className="mt-4 space-y-2 list-disc pl-6">
                        <li>Semantic HTML throughout the site.</li>
                        <li>Keyboard navigability with visible focus indicators.</li>
                        <li>Sufficient color contrast for body text against backgrounds.</li>
                        <li>Descriptive alternative text on all meaningful images.</li>
                        <li>Labels and ARIA attributes for all form controls and icon-only buttons.</li>
                        <li>Skip-to-content link for keyboard and screen-reader users.</li>
                        <li>Respect for <code>prefers-reduced-motion</code>.</li>
                        <li>Accessible age-verification modal with focus trapping.</li>
                    </ul>

                    <h2 className="font-display text-white mt-12" style={{ fontSize: 26, fontWeight: 500 }}>
                        Known limitations
                    </h2>
                    <p className="mt-4">
                        Our online shop is hosted on a third-party platform at{" "}
                        <a href={BIZ.shopUrl} target="_blank" rel="noopener noreferrer" className="link-underline text-white">
                            shop.liquor.studio
                        </a>. We work with that platform to improve its accessibility, but some
                        elements are outside our direct control.
                    </p>

                    <h2 className="font-display text-white mt-12" style={{ fontSize: 26, fontWeight: 500 }}>
                        Report an issue
                    </h2>
                    <p className="mt-4">
                        If you encounter any accessibility barrier on this site — anything that
                        prevents you from using it as intended — please tell us. We will respond
                        within five business days and work with you on a solution.
                    </p>
                    <ul className="mt-4 space-y-2 list-disc pl-6">
                        <li>
                            Email:{" "}
                            <a href={`mailto:${BIZ.email}`} className="link-underline text-white">
                                {BIZ.email}
                            </a>
                        </li>
                        <li>
                            Phone:{" "}
                            <a href={`tel:${BIZ.phoneTel}`} className="link-underline text-white">
                                {BIZ.phone}
                            </a>
                        </li>
                        <li>Or visit us in person at {BIZ.address.full}.</li>
                    </ul>
                </div>
            </section>
        </>
    );
}
